import { z } from "zod";

// Legacy clinician sign-up form (Story 16.1, Screen 00). Mirrors the
// register-legacy backend schema: names + work email + a required prior-AMPS-
// certification attestation. There is NO password field — the server generates a
// temporary one and emails it (the user sets their own on first sign-in).
//
// `prior_certification_confirmed` enforces the literal-true attestation: the
// checkbox must be ticked (false/unchecked → invalid), so the primary CTA stays
// disabled until it is checked (AC7). Modelled as boolean + refine rather than
// z.literal(true) so the unchecked default (false) is a valid form-input value for
// React Hook Form while validation still requires exactly true.
export const legacySignUpSchema = z.object({
  first_name: z.string().trim().min(1, "Enter your first name.").max(255),
  last_name: z.string().trim().min(1, "Enter your last name.").max(255),
  email: z.string().min(1, "Enter your work email.").email("Enter a valid email address."),
  prior_certification_confirmed: z
    .boolean()
    .refine((value) => value === true, {
      message: "You must confirm your prior AMPS certification.",
    }),
  // Story 16.8 (CR #373). Two SEPARATE acknowledgements, not one combined box:
  // they are different undertakings — accepting CITS's own documents, versus
  // accepting the clinician's own data-minimisation duty — and a single box
  // would make the compliance record unable to say which was accepted. Same
  // boolean + refine shape as the attestation above, and for the same reason;
  // the CTA stays disabled until BOTH are ticked (AC2).
  //
  // The server refuses a submission missing either one with 422 LEGACY-004 and
  // writes no user rows, so this is a courtesy gate, never the enforcement.
  document_acknowledgement_confirmed: z
    .boolean()
    .refine((value) => value === true, {
      message: "You must acknowledge the CITS legal documents.",
    }),
  data_responsibility_confirmed: z
    .boolean()
    .refine((value) => value === true, {
      message: "You must confirm your responsibility for client data.",
    }),
  // Story 23.2 (FR89): self-declared association membership. Optional, defaults to
  // 'none'. Does not block submit — the radio group is informational, not an
  // attestation. The four valid values mirror the backend Literal enum.
  association: z.enum(["otbc", "saot", "osot", "none"]).default("none"),
});

export type LegacySignUpValues = z.infer<typeof legacySignUpSchema>;
